import React, { useState, useCallback, useMemo, useEffect } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  ReactFlowProvider,
} from 'reactflow';
import 'reactflow/dist/style.css';
import SplitPane from 'react-split-pane-v2';
import './SchemaVisualizer.css';

const SchemaVisualizer = ({ sourceSchema, targetSchema, mappingResults = null }) => {
  const [sourceNodes, setSourceNodes, onSourceNodesChange] = useNodesState([]);
  const [sourceEdges, setSourceEdges, onSourceEdgesChange] = useEdgesState([]);
  const [targetNodes, setTargetNodes, onTargetNodesChange] = useNodesState([]);
  const [targetEdges, setTargetEdges, onTargetEdgesChange] = useEdgesState([]);
  const [mappingEdges, setMappingEdges, onMappingEdgesChange] = useEdgesState([]);

  // Color and line type mapping based on confidence
  const getMappingStyle = (confidence) => {
    if (confidence >= 90) {
      return { stroke: '#10B981', strokeWidth: 3, strokeDasharray: 'none' }; // Green solid
    } else if (confidence >= 70) {
      return { stroke: '#F59E0B', strokeWidth: 2, strokeDasharray: '5,5' }; // Orange dashed
    } else if (confidence >= 50) {
      return { stroke: '#EF4444', strokeWidth: 2, strokeDasharray: '10,5' }; // Red dotted
    } else {
      return { stroke: '#6B7280', strokeWidth: 1, strokeDasharray: '2,2' }; // Gray thin
    }
  };

  // Custom node component for database tables
  const TableNode = ({ data }) => {
    // Check if this table is involved in mappings
    const hasMappings = mappingResults && mappingResults.some(mapping => 
      (mapping.sourcetable && mapping.sourcetable.toLowerCase() === data.tableName.toLowerCase()) ||
      (mapping.targettable && mapping.targettable.toLowerCase() === data.tableName.toLowerCase())
    );

    return (
      <div className={`table-node ${hasMappings ? 'table-with-mappings' : ''}`}>
        <div className="table-header">
          <h4>{data.tableName}</h4>
          {hasMappings && <span className="mapping-indicator">📊</span>}
        </div>
        <div className="table-content">
          {data.columns.map((column, index) => {
            // Check if this column is involved in mappings
            const columnMappings = mappingResults && mappingResults.filter(mapping => 
              (mapping.source_column && mapping.source_column.toLowerCase() === column.name.toLowerCase()) ||
              (mapping.target_column && mapping.target_column.toLowerCase() === column.name.toLowerCase())
            );
            
            const hasColumnMappings = columnMappings && columnMappings.length > 0;
            
            return (
              <div key={index} className={`column-row ${hasColumnMappings ? 'column-with-mappings' : ''}`}>
                <span className="column-name">{column.name}</span>
                <span className="column-type">{column.data_type}</span>
                <span className="column-icons">
                  {column.is_primary_key && <span className="icon pk-icon">🔑</span>}
                  {column.is_foreign_key && <span className="icon fk-icon">🔗</span>}
                  {hasColumnMappings && <span className="icon mapping-icon">📈</span>}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const nodeTypes = useMemo(() => ({
    tableNode: TableNode,
  }), []);

  // Helper function to validate edge
  const isValidEdge = (edge) => {
    return edge && 
           edge.id && 
           edge.source && 
           edge.target && 
           edge.source !== edge.target && 
           edge.source !== 'undefined' && 
           edge.target !== 'undefined' &&
           typeof edge.source === 'string' &&
           typeof edge.target === 'string';
  };

  // Helper function to check if node exists
  const nodeExists = (nodeId, nodes) => {
    return nodes.some(node => node.id === nodeId);
  };

  // Process schema data to create nodes and edges with better positioning
  const processSchema = useCallback((schema, isSource = true) => {
    if (!schema || !schema.tables) return { nodes: [], edges: [] };

    const nodes = [];
    const edges = [];
    let edgeId = 0;

    // Create nodes for each table with better positioning
    schema.tables.forEach((table, tableIndex) => {
      // Calculate position: arrange in a grid layout
      const nodesPerRow = 3; // More tables per row for better space utilization
      const row = Math.floor(tableIndex / nodesPerRow);
      const col = tableIndex % nodesPerRow;
      
      // Adjust positioning for combined view with better separation
      const baseX = isSource ? 50 : 1200; // More separation between source and target
      const baseY = 100;
      
      const node = {
        id: `table-${isSource ? 'source' : 'target'}-${tableIndex}`,
        type: 'tableNode',
        position: { 
          x: baseX + col * 300, // Slightly smaller spacing
          y: baseY + row * 200  // Slightly smaller vertical spacing
        },
        data: {
          tableName: table.name,
          columns: table.attributes || [],
        },
      };
      nodes.push(node);

      // Create edges for foreign key relationships (only within same schema)
      if (table.attributes) {
        table.attributes.forEach((column, colIndex) => {
          if (column.is_foreign_key && column.references) {
            // Find the referenced table index
            const referencedTableIndex = schema.tables.findIndex(t => 
              t.name === column.references.table
            );
            
            if (referencedTableIndex !== -1) {
              const sourceNodeId = `table-${isSource ? 'source' : 'target'}-${tableIndex}`;
              const targetNodeId = `table-${isSource ? 'source' : 'target'}-${referencedTableIndex}`;
              
              const edge = {
                id: `edge-${isSource ? 'source' : 'target'}-${edgeId++}`,
                source: sourceNodeId,
                target: targetNodeId,
                type: 'smoothstep',
                animated: true,
                style: { stroke: '#ff6b6b', strokeWidth: 2 },
              };
              
              // Only add valid edges
              if (isValidEdge(edge)) {
                edges.push(edge);
              } else {
                console.warn('Skipping invalid foreign key edge:', edge);
              }
            }
          }
        });
      }
    });

    return { nodes, edges };
  }, []);

  // Process mapping results to create mapping edges
  const processMappingResults = useCallback((mappings, sourceNodes, targetNodes) => {
    if (!mappings || !Array.isArray(mappings) || !sourceNodes || !targetNodes) {
      console.log('Invalid mappings or nodes:', { mappings, sourceNodes, targetNodes });
      return [];
    }

    console.log('Processing mappings:', mappings);
    const mappingEdges = [];
    let edgeId = 0;

    // Create a lookup for existing nodes
    const sourceNodeIds = sourceNodes.map(node => node.id);
    const targetNodeIds = targetNodes.map(node => node.id);
    
    console.log('Available source node IDs:', sourceNodeIds);
    console.log('Available target node IDs:', targetNodeIds);

    // Group mappings by source and target tables
    const tableMappings = {};
    
    mappings.forEach((mapping, index) => {
      console.log(`Processing mapping ${index}:`, mapping);
      
      // Handle different possible field names for source and target columns
      const sourceColumn = mapping.source_column || mapping.sourcecolumn || mapping.sourceColumn;
      const targetColumn = mapping.target_column || mapping.targetcolumn || mapping.targetColumn;
      
      if (sourceColumn && targetColumn) {
        const sourceTable = mapping.sourcetable || mapping.source_table || mapping.sourceTable || 'unknown_source';
        const targetTable = mapping.targettable || mapping.target_table || mapping.targetTable || 'unknown_target';
        
        console.log(`Found mapping: ${sourceTable}.${sourceColumn} -> ${targetTable}.${targetColumn}`);
        
        const key = `${sourceTable}-${targetTable}`;
        if (!tableMappings[key]) {
          tableMappings[key] = [];
        }
        tableMappings[key].push({
          ...mapping,
          source_column: sourceColumn,
          target_column: targetColumn,
          sourcetable: sourceTable,
          targettable: targetTable
        });
      } else {
        console.log('Skipping mapping - missing source or target column:', mapping);
      }
    });

    // Create edges for each table-to-table mapping
    Object.entries(tableMappings).forEach(([key, tableMapping]) => {
      const [sourceTable, targetTable] = key.split('-');
      if (!sourceTable || !targetTable) {
        console.warn('Skipping mapping with missing table names:', key, tableMapping);
        return;
      }
      
      // Normalize table names for comparison
      const normalizedSourceTable = sourceTable.toLowerCase().trim();
      const normalizedTargetTable = targetTable.toLowerCase().trim();
      
      console.log('Looking for tables:', { 
        sourceTable: normalizedSourceTable, 
        targetTable: normalizedTargetTable,
        availableSourceTables: sourceSchema?.tables?.map(t => t.name.toLowerCase()),
        availableTargetTables: targetSchema?.tables?.map(t => t.name.toLowerCase())
      });
      
      const sourceNodeIndex = sourceSchema?.tables?.findIndex(table => 
        table.name.toLowerCase().trim() === normalizedSourceTable
      );
      const targetNodeIndex = targetSchema?.tables?.findIndex(table => 
        table.name.toLowerCase().trim() === normalizedTargetTable
      );
      
      if (sourceNodeIndex === -1 || targetNodeIndex === -1) {
        console.warn('Skipping mapping edge: table not found in schema', { 
          sourceTable: normalizedSourceTable, 
          targetTable: normalizedTargetTable, 
          sourceNodeIndex, 
          targetNodeIndex,
          sourceSchemaTables: sourceSchema?.tables?.map(t => t.name),
          targetSchemaTables: targetSchema?.tables?.map(t => t.name)
        });
        return;
      }
      
      const sourceNodeId = `table-source-${sourceNodeIndex}`;
      const targetNodeId = `table-target-${targetNodeIndex}`;
      
      // Validate that the nodes actually exist
      if (!nodeExists(sourceNodeId, sourceNodes) || !nodeExists(targetNodeId, targetNodes)) {
        console.warn('Skipping mapping edge: nodes do not exist', { 
          sourceNodeId, 
          targetNodeId,
          sourceNodeExists: nodeExists(sourceNodeId, sourceNodes),
          targetNodeExists: nodeExists(targetNodeId, targetNodes),
          sourceNodeIds,
          targetNodeIds
        });
        return;
      }

      console.log(`Creating edge from ${sourceNodeId} to ${targetNodeId}`);

      // Calculate average confidence for this table mapping
      const avgConfidence = tableMapping.reduce((sum, m) => sum + (m.confidence || 0), 0) / tableMapping.length;

      // Create the main table-to-table edge
      const edge = {
        id: `mapping-edge-${edgeId++}`,
        source: sourceNodeId,
        target: targetNodeId,
        type: 'smoothstep',
        animated: false,
        style: getMappingStyle(avgConfidence),
        label: `${sourceTable} → ${targetTable}`,
        labelStyle: { fontSize: '12px', fill: '#374151', fontWeight: 'bold' },
        labelBgStyle: { fill: '#F9FAFB', fillOpacity: 0.9 },
      };
      
      // Validate and add edge
      if (isValidEdge(edge)) {
        mappingEdges.push(edge);
      } else {
        console.warn('Skipping invalid mapping edge:', edge);
      }

      // Add individual column mappings as smaller edges (limit to prevent clutter)
      const maxColumnEdges = 3; // Limit column edges to prevent visual clutter
      tableMapping.slice(0, maxColumnEdges).forEach((mapping, index) => {
        const columnEdge = {
          id: `mapping-column-${edgeId++}`,
          source: sourceNodeId,
          target: targetNodeId,
          type: 'smoothstep',
          animated: false,
          style: {
            ...getMappingStyle(mapping.confidence),
            strokeWidth: 1,
            strokeDasharray: '3,3',
            opacity: 0.6
          },
          label: `${mapping.source_column} → ${mapping.target_column}`,
          labelStyle: { fontSize: '8px', fill: '#6B7280' },
          labelBgStyle: { fill: '#F9FAFB', fillOpacity: 0.7 },
        };
        
        // Validate and add column edge
        if (isValidEdge(columnEdge)) {
          mappingEdges.push(columnEdge);
        } else {
          console.warn('Skipping invalid column mapping edge:', columnEdge);
        }
      });
    });

    console.log('Final mapping edges:', mappingEdges);
    return mappingEdges;
  }, [sourceSchema, targetSchema, getMappingStyle]);

  // Initialize nodes and edges when schemas change
  useEffect(() => {
    console.log('Processing schemas:', { sourceSchema, targetSchema });
    const sourceData = processSchema(sourceSchema, true);
    const targetData = processSchema(targetSchema, false);
    
    console.log('Source data:', sourceData);
    console.log('Target data:', targetData);
    
    setSourceNodes(sourceData.nodes);
    setSourceEdges(sourceData.edges);
    setTargetNodes(targetData.nodes);
    setTargetEdges(targetData.edges);
  }, [sourceSchema, targetSchema, processSchema]);

  // Update mapping edges when mapping results change
  useEffect(() => {
    // Add a small delay to ensure nodes are properly initialized
    const timeoutId = setTimeout(() => {
      if (mappingResults && mappingResults.length > 0 && sourceNodes.length > 0 && targetNodes.length > 0) {
        console.log('Processing mapping results:', mappingResults);
        const newMappingEdges = processMappingResults(mappingResults, sourceNodes, targetNodes);
        console.log('Generated mapping edges:', newMappingEdges);
        setMappingEdges(newMappingEdges);
      } else {
        console.log('No mapping results or nodes not ready', {
          mappingResults: mappingResults?.length,
          sourceNodes: sourceNodes.length,
          targetNodes: targetNodes.length
        });
        setMappingEdges([]);
      }
    }, 200); // Increased delay to ensure nodes are fully initialized

    return () => clearTimeout(timeoutId);
  }, [mappingResults, processMappingResults, sourceNodes, targetNodes]);

  const onSourceConnect = useCallback(
    (params) => setSourceEdges((eds) => addEdge(params, eds)),
    [setSourceEdges]
  );

  const onTargetConnect = useCallback(
    (params) => setTargetEdges((eds) => addEdge(params, eds)),
    [setTargetEdges]
  );

  const onMappingConnect = useCallback(
    (params) => setMappingEdges((eds) => addEdge(params, eds)),
    [setMappingEdges]
  );

  return (
    <div className="schema-visualizer">
      {/* Always show combined view when we have both schemas */}
      <div className="schema-pane combined-pane">
        <div className="pane-header">
          <h3>ETL Mapping Visualization</h3>
          <span className="schema-name">
            {sourceSchema?.name} → {targetSchema?.name}
          </span>
        </div>
        
        {/* Mapping Summary Panel */}
        {mappingResults && mappingResults.length > 0 ? (
          <div className="mapping-summary">
            <div className="summary-stats">
              <div className="stat-item">
                <span className="stat-label">Total Mappings:</span>
                <span className="stat-value">{mappingResults.length}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Source Tables:</span>
                <span className="stat-value">
                  {new Set(mappingResults.map(m => m.sourcetable || m.source_table).filter(Boolean)).size}
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Target Tables:</span>
                <span className="stat-value">
                  {new Set(mappingResults.map(m => m.targettable || m.target_table).filter(Boolean)).size}
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Avg Confidence:</span>
                <span className="stat-value">
                  {(mappingResults.reduce((sum, m) => sum + (m.confidence || 0), 0) / mappingResults.length).toFixed(2)}
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Mapping Edges:</span>
                <span className="stat-value">{mappingEdges.length}</span>
              </div>
            </div>
            
            {/* Debug Info */}
            <div className="debug-info" style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
              <details>
                <summary>Debug Info</summary>
                <div style={{ marginTop: '5px' }}>
                  <div>Source Nodes: {sourceNodes.length}</div>
                  <div>Target Nodes: {targetNodes.length}</div>
                  <div>Source Edges: {sourceEdges.length}</div>
                  <div>Target Edges: {targetEdges.length}</div>
                  <div>Mapping Edges: {mappingEdges.length}</div>
                  <div>Total Edges: {sourceEdges.length + targetEdges.length + mappingEdges.length}</div>
                </div>
              </details>
            </div>
          </div>
        ) : (
          <div className="mapping-summary">
            <div className="summary-stats">
              <div className="stat-item">
                <span className="stat-label">Status:</span>
                <span className="stat-value">No Mappings</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Source Tables:</span>
                <span className="stat-value">{sourceSchema?.tables?.length || 0}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Target Tables:</span>
                <span className="stat-value">{targetSchema?.tables?.length || 0}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Mapping Edges:</span>
                <span className="stat-value">{mappingEdges.length}</span>
              </div>
            </div>
            <div style={{ textAlign: 'center', marginTop: '10px', color: '#666', fontSize: '14px' }}>
              Generate ETL mapping to see connections between schemas
            </div>
          </div>
        )}
          
          <div className="flow-container">
            <ReactFlowProvider>
              {(() => {
                const allNodes = [...sourceNodes, ...targetNodes];
                const allEdges = [...sourceEdges, ...targetEdges, ...mappingEdges];
                
                // Double-check all edges are valid before passing to React Flow
                const validEdges = allEdges.filter(edge => {
                  const isValid = isValidEdge(edge) && 
                                 nodeExists(edge.source, allNodes) && 
                                 nodeExists(edge.target, allNodes);
                  if (!isValid) {
                    console.warn('Filtering out invalid edge:', edge);
                  }
                  return isValid;
                });
                
                console.log('ReactFlow nodes:', allNodes);
                console.log('ReactFlow edges (filtered):', validEdges);
                console.log('Filtered out edges:', allEdges.length - validEdges.length);
                
                return (
                  <ReactFlow
                    nodes={allNodes}
                    edges={validEdges}
                    onNodesChange={(changes) => {
                      // Handle all node changes
                      const sourceChanges = changes.filter(c => c.id?.startsWith('table-source'));
                      const targetChanges = changes.filter(c => c.id?.startsWith('table-target'));
                      
                      if (sourceChanges.length > 0) onSourceNodesChange(sourceChanges);
                      if (targetChanges.length > 0) onTargetNodesChange(targetChanges);
                    }}
                    onEdgesChange={(changes) => {
                      // Handle all edge changes
                      const sourceChanges = changes.filter(c => c.id?.startsWith('edge-source'));
                      const targetChanges = changes.filter(c => c.id?.startsWith('edge-target'));
                      const mappingChanges = changes.filter(c => c.id?.startsWith('mapping-edge') || c.id?.startsWith('mapping-column'));
                      
                      if (sourceChanges.length > 0) onSourceEdgesChange(sourceChanges);
                      if (targetChanges.length > 0) onTargetEdgesChange(targetChanges);
                      if (mappingChanges.length > 0) onMappingEdgesChange(mappingChanges);
                    }}
                    onConnect={onSourceConnect}
                    nodeTypes={nodeTypes}
                    fitView
                    fitViewOptions={{ padding: 0.1 }}
                    attributionPosition="bottom-left"
                    defaultEdgeOptions={{
                      type: 'smoothstep',
                      animated: false,
                    }}
                  >
                    <Controls />
                    <MiniMap />
                    <Background variant="dots" gap={12} size={1} />
                  </ReactFlow>
                );
              })()}
            </ReactFlowProvider>
          </div>
        </div>
      
      {/* Mapping Legend */}
      {mappingResults && mappingResults.length > 0 && (
        <div className="mapping-legend">
          <h4>Mapping Legend</h4>
          <div className="legend-items">
            <div className="legend-item">
              <div className="legend-line" style={{ stroke: '#10B981', strokeWidth: 3 }}></div>
              <span>High Confidence (90%+)</span>
            </div>
            <div className="legend-item">
              <div className="legend-line" style={{ stroke: '#F59E0B', strokeWidth: 2, strokeDasharray: '5,5' }}></div>
              <span>Medium Confidence (70-89%)</span>
            </div>
            <div className="legend-item">
              <div className="legend-line" style={{ stroke: '#EF4444', strokeWidth: 2, strokeDasharray: '10,5' }}></div>
              <span>Low Confidence (50-69%)</span>
            </div>
            <div className="legend-item">
              <div className="legend-line" style={{ stroke: '#6B7280', strokeWidth: 1, strokeDasharray: '2,2' }}></div>
              <span>Very Low Confidence (&lt;50%)</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchemaVisualizer;